import { useMemo, useState } from 'react';
import {
  Loader2,
  Minus,
  MousePointerClick,
  Pencil,
  Plus,
  Search,
  Trash2,
  Truck
} from 'lucide-react';
import type { LocationSupplier } from '../Types';
import { cn } from '@/shared/utils/helpers';
import {
  onboardingDualPanelGridClass,
  onboardingStepShellClass
} from '../Utils/onboardingLayout';
import type { ListedInventoryItem } from './AddItemsForm';

const checkboxClass =
  'h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-2 border-[#b9bfc7] bg-white transition hover:border-[#087f8c] checked:border-[#087f8c] checked:bg-[#087f8c] indeterminate:border-[#087f8c] indeterminate:bg-[#087f8c]/30 disabled:cursor-not-allowed disabled:opacity-40';

const supplierCheckboxClass =
  'h-[18px] w-[18px] shrink-0 cursor-pointer rounded border-2 border-[#b9bfc7] bg-white transition hover:border-[#087f8c] checked:border-[#087f8c] checked:bg-[#087f8c] disabled:cursor-not-allowed disabled:opacity-40';

const getDisplayStockUnit = (unit: string): string => {
  const trimmed = unit.trim();
  return /^val(?:ue)?(?:\s|$)/i.test(trimmed) ? '' : trimmed;
};

const supplierDisplayName = (supplier: LocationSupplier) =>
  supplier.business_name?.trim() || 'Unnamed supplier';

interface SuppliersStepProps {
  items: ListedInventoryItem[];
  suppliers: LocationSupplier[];
  onBulkAssignSupplier: (
    items: ListedInventoryItem[],
    supplierUserId: string
  ) => Promise<void>;
  onRequestAddSupplier?: () => void;
  onNext: () => void;
  onPrev: () => void;
}

export const SuppliersStep = ({
  items,
  suppliers,
  onBulkAssignSupplier,
  onRequestAddSupplier,
  onNext,
  onPrev
}: SuppliersStepProps) => {
  const [filter, setFilter] = useState<'all' | 'no_supplier'>('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [targetSupplierId, setTargetSupplierId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const activeSuppliers = useMemo(
    () =>
      suppliers.filter(supplier => supplier.active && !supplier.is_archived),
    [suppliers]
  );

  const targetSupplierName = useMemo(() => {
    if (!targetSupplierId) return null;
    const match = activeSuppliers.find(
      supplier => supplier.supplier_id === targetSupplierId
    );
    return match ? supplierDisplayName(match) : null;
  }, [activeSuppliers, targetSupplierId]);

  const itemHasNoSupplier = (item: ListedInventoryItem) =>
    !item.supplierName?.trim();

  const noSupplierCount = useMemo(
    () => items.filter(itemHasNoSupplier).length,
    [items]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items
      .filter(item => {
        if (query && !item.name.toLowerCase().includes(query)) return false;
        if (filter === 'no_supplier') return itemHasNoSupplier(item);
        return true;
      })
      .sort((a, b) => {
        // No-supplier items first so the work queue is visible immediately.
        const aMissing = itemHasNoSupplier(a) ? 0 : 1;
        const bMissing = itemHasNoSupplier(b) ? 0 : 1;
        if (aMissing !== bMissing) return aMissing - bMissing;
        return a.name.localeCompare(b.name);
      });
  }, [filter, items, search]);

  const selectedItems = useMemo(
    () => filteredItems.filter(item => selectedIds.has(item.localId)),
    [filteredItems, selectedIds]
  );

  const selectionEnabled = selectedItems.length > 0;

  const allSelected =
    filteredItems.length > 0 &&
    filteredItems.every(item => selectedIds.has(item.localId));

  const toggleItem = (localId: string) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      filteredItems.forEach(item => {
        if (checked) next.add(item.localId);
        else next.delete(item.localId);
      });
      return next;
    });
  };

  const selectTargetSupplier = (supplier: LocationSupplier) => {
    setTargetSupplierId(previous =>
      previous === supplier.supplier_id ? null : supplier.supplier_id
    );
  };

  const applySupplier = async () => {
    if (!targetSupplierId || selectedItems.length === 0) return;
    setBusy(true);
    try {
      await onBulkAssignSupplier(selectedItems, targetSupplierId);
      setSelectedIds(new Set());
      setTargetSupplierId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={onboardingStepShellClass}>
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
          Suppliers
        </h2>
        <p className="mt-1 text-sm text-[#6f747c]">
          Select items, check a supplier, then assign. You can change this later.
        </p>
      </div>

      <div className={onboardingDualPanelGridClass}>
        <section className="order-2 flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-[#e7e9ee] bg-white shadow-sm lg:order-1 lg:h-full">
          <div className="shrink-0 border-b border-[#e7e9ee] px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-[#171717]">
                Your items
              </h3>
              <span className="text-[11px] text-[#8a8f96]">
                {noSupplierCount > 0
                  ? `${noSupplierCount} no supplier`
                  : 'All assigned'}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-[#8a8f96]">
              Select items, then check a supplier on the right.
            </p>
          </div>

          <div className="shrink-0 space-y-2 border-b border-[#e7e9ee] p-3">
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  { id: 'all', label: 'All', count: items.length },
                  {
                    id: 'no_supplier',
                    label: 'No supplier',
                    count: noSupplierCount
                  }
                ] as const
              ).map(itemFilter => (
                <button
                  key={itemFilter.id}
                  type="button"
                  onClick={() => setFilter(itemFilter.id)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition',
                    filter === itemFilter.id
                      ? 'border-[#087f8c] bg-[#087f8c] text-white'
                      : 'border-[#e1e4e8] bg-white text-[#5f646b] hover:border-[#087f8c]/50'
                  )}
                >
                  {itemFilter.label} ({itemFilter.count})
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#8a8f96]" />
              <input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search items"
                className="h-8 w-full rounded-md border border-[#e7e9ee] bg-white pl-8 pr-3 text-xs outline-none focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/15"
              />
            </div>
          </div>

          <div className="min-h-0 max-h-[min(68vh,620px)] flex-1 space-y-2 overflow-y-auto bg-[#f7f8f9] p-3 lg:max-h-none">
            {items.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#dfe3e7] bg-white px-4 py-8 text-center text-sm text-[#6f747c]">
                No items yet. Go back and add some first.
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#dfe3e7] bg-white px-4 py-8 text-center text-sm text-[#6f747c]">
                {search.trim()
                  ? 'No items match your search.'
                  : 'Every item already has a supplier.'}
              </div>
            ) : (
              <>
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#e7e9ee] bg-white px-3 py-2 text-[11px] font-semibold text-[#5f646b]">
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={allSelected}
                    disabled={busy}
                    ref={element => {
                      if (element) {
                        element.indeterminate =
                          selectedItems.length > 0 && !allSelected;
                      }
                    }}
                    onChange={event => toggleSelectAll(event.target.checked)}
                  />
                  Select all ({filteredItems.length})
                </label>

                {filteredItems.map(item => {
                  const isSelected = selectedIds.has(item.localId);
                  const hasSupplier = Boolean(item.supplierName?.trim());
                  const quantity = Math.max(
                    0,
                    Number(item.quantityOnHand ?? 0)
                  );
                  const displayStockUnit = getDisplayStockUnit(item.stockUnit);

                  return (
                    <article
                      key={item.localId}
                      className={cn(
                        'rounded-lg border bg-white p-3 shadow-sm transition',
                        isSelected
                          ? 'border-[#087f8c]/60 bg-[#087f8c]/[0.04]'
                          : 'border-[#e7e9ee]'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className={checkboxClass}
                          checked={isSelected}
                          disabled={busy}
                          onChange={() => toggleItem(item.localId)}
                          aria-label={`Select ${item.name}`}
                        />

                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-sm font-semibold text-[#171717]">
                            {item.name}
                          </h3>
                          <p className="mt-0.5 text-[11px] text-[#6f747c]">
                            Min: {item.parLevel ?? 0}
                            {displayStockUnit ? ` ${displayStockUnit}` : ''}
                          </p>
                          <span
                            className={cn(
                              'mt-1 inline-flex items-center gap-1 text-[11px] font-semibold',
                              hasSupplier ? 'text-[#087f8c]' : 'text-[#d16b68]'
                            )}
                          >
                            <Truck className="h-3 w-3" />
                            {hasSupplier ? item.supplierName : 'No supplier'}
                          </span>
                        </div>

                        <div className="flex shrink-0 items-center gap-1">
                          <span
                            title="Edit quantity in Items step"
                            className="inline-flex"
                          >
                            <button
                              type="button"
                              disabled
                              className="pointer-events-none grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white opacity-40"
                              aria-label={`Decrease ${item.name}`}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                          </span>
                          <div
                            title={`${quantity}${displayStockUnit ? ` ${displayStockUnit}` : ''}`}
                            className="flex h-8 w-[78px] shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border border-[#dfe3e7] bg-white px-1.5 text-xs font-bold tabular-nums text-[#087f8c]"
                          >
                            <span className="shrink-0">{quantity}</span>
                            {displayStockUnit && (
                              <span className="min-w-0 truncate font-medium text-[#6f747c]">
                                {displayStockUnit}
                              </span>
                            )}
                          </div>
                          <span
                            title="Edit quantity in Items step"
                            className="inline-flex"
                          >
                            <button
                              type="button"
                              disabled
                              className="pointer-events-none grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white opacity-40"
                              aria-label={`Increase ${item.name}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </span>
                          <div className="ml-0.5 flex flex-col gap-1">
                            <span
                              title="Edit in Items step"
                              className="inline-flex"
                            >
                              <button
                                type="button"
                                disabled
                                className="pointer-events-none grid h-7 w-7 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#9a9fa6] opacity-40"
                                aria-label={`Edit ${item.name}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </span>
                            <span
                              title="Remove in Items step"
                              className="inline-flex"
                            >
                              <button
                                type="button"
                                disabled
                                className="pointer-events-none grid h-7 w-7 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#9a9fa6] opacity-40"
                                aria-label={`Remove ${item.name}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </>
            )}
          </div>
        </section>

        <section className="order-1 flex min-h-0 flex-col space-y-3 overflow-hidden rounded-lg border border-[#e7e9ee] bg-white p-5 shadow-sm lg:order-2 lg:h-full">
          <div className="flex shrink-0 items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-[#171717]">
                Suppliers
              </h3>
              <p className="mt-0.5 text-[11px] text-[#8a8f96]">
                {activeSuppliers.length} active supplier
                {activeSuppliers.length === 1 ? '' : 's'}
              </p>
            </div>
            {onRequestAddSupplier && (
              <button
                type="button"
                onClick={onRequestAddSupplier}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-[#087f8c] px-2.5 text-xs font-bold text-white transition hover:bg-[#066873]"
              >
                <Plus className="h-3.5 w-3.5" />
                Add supplier
              </button>
            )}
          </div>

          {selectionEnabled ? (
            <div className="flex shrink-0 items-start gap-2 rounded-md border border-[#087f8c]/25 bg-[#087f8c]/[0.06] px-3 py-2">
              <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#087f8c]" />
              <p className="text-[11px] leading-relaxed text-[#087f8c]">
                <span className="font-bold">
                  {selectedItems.length} item
                  {selectedItems.length === 1 ? '' : 's'} selected.
                </span>{' '}
                Hover any supplier, check the box on the left, then assign
                below.
              </p>
            </div>
          ) : (
            <div className="flex shrink-0 items-start gap-2 rounded-md border border-[#e7e9ee] bg-[#fafbfc] px-3 py-2">
              <MousePointerClick className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8a8f96]" />
              <p className="text-[11px] leading-relaxed text-[#6f747c]">
                <span className="font-semibold text-[#5f646b]">
                  How to assign:
                </span>{' '}
                select items on the left → check a supplier here → press Assign.
              </p>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {activeSuppliers.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#dfe3e7] px-4 py-8 text-center">
                <p className="text-sm font-semibold text-[#171717]">
                  No suppliers yet
                </p>
                <p className="mt-1 text-xs text-[#6f747c]">
                  Add a supplier to assign it to your items.
                </p>
              </div>
            ) : (
              <ul className="space-y-0.5 rounded-md border border-[#e7e9ee] bg-[#fafbfc] p-1.5">
                {activeSuppliers.map(supplier => {
                  const name = supplierDisplayName(supplier);
                  const isSelected = targetSupplierId === supplier.supplier_id;

                  return (
                    <li key={supplier.supplier_id}>
                      <div
                        className={cn(
                          'group flex items-center gap-2 rounded-md px-2 py-2 transition',
                          isSelected
                            ? 'bg-[#087f8c]/[0.08] ring-1 ring-[#087f8c]/25'
                            : 'hover:bg-[#087f8c]/[0.05]'
                        )}
                      >
                        {selectionEnabled ? (
                          <label
                            title={`Assign to ${name}`}
                            className={cn(
                              'grid h-6 w-6 shrink-0 place-items-center transition',
                              isSelected
                                ? 'opacity-100'
                                : 'opacity-40 group-hover:opacity-100 focus-within:opacity-100'
                            )}
                          >
                            <input
                              type="checkbox"
                              className={supplierCheckboxClass}
                              checked={isSelected}
                              disabled={busy}
                              onChange={() => selectTargetSupplier(supplier)}
                              aria-label={`Select ${name}`}
                            />
                          </label>
                        ) : (
                          <span className="grid h-6 w-6 shrink-0 place-items-center">
                            <Truck className="h-3.5 w-3.5 text-[#087f8c]" />
                          </span>
                        )}

                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#171717]">
                          {name}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="shrink-0 border-t border-[#e7e9ee] pt-3">
            <button
              type="button"
              disabled={!targetSupplierId || selectedItems.length === 0 || busy}
              onClick={() => void applySupplier()}
              className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#087f8c] px-3 text-sm font-bold text-white transition hover:bg-[#066873] disabled:cursor-not-allowed disabled:opacity-45"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : selectedItems.length === 0 ? (
                'Select items on the left first'
              ) : !targetSupplierId ? (
                'Check a supplier above'
              ) : (
                <>
                  Assign {selectedItems.length} item
                  {selectedItems.length === 1 ? '' : 's'} to{' '}
                  <span className="font-semibold opacity-90">
                    {targetSupplierName}
                  </span>
                </>
              )}
            </button>
          </div>
        </section>
      </div>

      <div className="flex items-center gap-3 border-t border-[#e7e9ee] pt-4">
        <button
          type="button"
          onClick={onPrev}
          className="rounded-md px-3 py-2 text-sm font-medium text-[#6f747c] transition hover:bg-[#f0f2f5] hover:text-[#171717]"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="ml-auto rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#066873]"
        >
          Continue to review
        </button>
      </div>
    </div>
  );
};
